import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Escalation, useUpdateEscalation } from "@/lib/api/escalations";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { cn, formatDate } from "@/lib/utils";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

interface EscalationNoteFields {
  escalationType: "Crisis" | "Escalation" | "Other";
  crisisSubType?: "SH" | "SI" | "SA" | "HI" | "Other" | "";
  wasReferral: boolean;
  referralType?: string;
  reasonForEscalation?: string;
  explainReasonForAlert?: string;
  suicideAssessmentRisk?: string;
  informationProvidedByMentor?: string;
  conversationsWithEmergencyContact?: string;
  nextSteps: string;
  safetyPlan?: string;
  additionalNotes?: string;
}

interface EscalationNoteProps {
  mentorship: MentorshipDetails;
  escalation: Escalation;
  onClose: () => void;
}

const ESCALATION_TYPES = [
  { value: "Crisis", label: "Crisis" },
  { value: "Escalation", label: "Escalation" },
  { value: "Other", label: "Other" },
] as const;

const CRISIS_SUBTYPES = [
  { value: "SH", label: "Self-Harm (SH)" },
  { value: "SI", label: "Suicidal Ideation (SI)" },
  { value: "SA", label: "Suicide Attempt (SA)" },
  { value: "HI", label: "Homicidal Ideation (HI)" },
  { value: "Other", label: "Other" },
] as const;

const REFERRAL_TYPES = [
  "Mental Health Professional",
  "Medical Provider",
  "Emergency Services",
  "Crisis Hotline",
  "School Counselor",
  "Family Services",
  "Substance Abuse Treatment",
  "Other",
] as const;

export function EscalationNote({
  mentorship,
  escalation,
  onClose,
}: EscalationNoteProps) {
  const { toast } = useToast();

  const { mutateAsync: updateEscalation } = useUpdateEscalation(
    mentorship?.mentee?.id as string,
  );

  const [completingEscalation, setCompletingEscalation] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EscalationNoteFields>({
    defaultValues: {
      escalationType: escalation.metadata?.escalationType || "",
      crisisSubType: escalation.metadata?.crisisSubType || "",
      wasReferral: escalation.metadata?.wasReferral || false,
      referralType: escalation.metadata?.referralType || "",
      reasonForEscalation: escalation.metadata?.reasonForEscalation || "",
      explainReasonForAlert: escalation.metadata?.explainReasonForAlert || "",
      suicideAssessmentRisk: escalation.metadata?.suicideAssessmentRisk || "",
      informationProvidedByMentor:
        escalation.metadata?.informationProvidedByMentor || "",
      conversationsWithEmergencyContact:
        escalation.metadata?.conversationsWithEmergencyContact || "",
      nextSteps:
        escalation.metadata?.nextSteps ||
        escalation.metadata?.nextStepsSafetyPlan ||
        "",
      safetyPlan: escalation.metadata?.safetyPlan || "",
      additionalNotes: escalation.metadata?.additionalNotes || "",
    },
    mode: "onBlur",
  });

  const watchedEscalationType = useWatch({ control, name: "escalationType" });
  const watchedWasReferral = useWatch({ control, name: "wasReferral" });

  const showFullForm =
    watchedEscalationType === "Crisis" ||
    watchedEscalationType === "Escalation";
  const showCrisisSubtype = watchedEscalationType === "Crisis";

  const handleFieldBlur = async (
    field: keyof EscalationNoteFields,
    value: string | boolean,
  ) => {
    try {
      await updateEscalation({
        id: escalation.id,
        metadata: {
          ...escalation.metadata,
          [field]: value,
        },
      });
    } catch (error) {
      console.error("Failed to update escalation metadata:", error);
    }
  };

  const onSubmit = async (data: EscalationNoteFields) => {
    setCompletingEscalation(true);
    await updateEscalation({
      id: escalation.id,
      metadata: {
        ...escalation.metadata,
        ...data,
      },
      status: "CLOSED",
    });

    setCompletingEscalation(false);
    onClose();

    toast({
      title: "Escalation has been closed",
      className: cn(
        "top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4",
      ),
    });
  };

  return (
    <div
      className={cn(
        "inline-flex w-full flex-col items-start justify-start gap-2.5 [&:not(:first-child)]:mt-4",
      )}
    >
      <div className="inline-flex items-center justify-start gap-2.5 self-stretch">
        <div className="flex items-center justify-start gap-2.5">
          <div className="flex items-center justify-start gap-2.5 rounded bg-rose-50">
            <div className="flex items-center justify-start gap-2.5 p-1">
              <AlertTriangle size={12} className="text-red-600" />
            </div>
          </div>
          <div className="text-xs font-normal text-stone-500">
            An escalation was started on {formatDate(escalation.created_at)} by{" "}
            {escalation.started_by?.name}
          </div>
        </div>
      </div>
      <div className="inline-flex items-start justify-start gap-2.5 self-stretch">
        <div className="flex w-4 items-start justify-start gap-2.5 self-stretch px-2">
          <div className="w-20 origin-top-left rotate-0 self-stretch border border-zinc-300"></div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <Card className="p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-black">
                  🚨 Escalation Note
                </h2>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {/* Escalation Type */}
              <div>
                <CustomLabel>Escalation Type *</CustomLabel>
                <Controller
                  name="escalationType"
                  control={control}
                  rules={{ required: "Escalation type is required" }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        handleFieldBlur("escalationType", value || "");
                      }}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select escalation type" />
                      </SelectTrigger>
                      <SelectContent>
                        {ESCALATION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.escalationType && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.escalationType.message}
                  </p>
                )}
              </div>

              {/* Crisis Sub-type (only shown for Crisis) */}
              {showCrisisSubtype && (
                <div>
                  <CustomLabel>Crisis Type *</CustomLabel>
                  <Controller
                    name="crisisSubType"
                    control={control}
                    rules={{ required: "Crisis type is required" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          handleFieldBlur("crisisSubType", value || "");
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select crisis type" />
                        </SelectTrigger>
                        <SelectContent>
                          {CRISIS_SUBTYPES.map((subtype) => (
                            <SelectItem
                              key={subtype.value}
                              value={subtype.value}
                            >
                              {subtype.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.crisisSubType && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.crisisSubType.message}
                    </p>
                  )}
                </div>
              )}

              {/* Was this a referral? */}
              <div className="flex items-center space-x-2">
                <Controller
                  name="wasReferral"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="wasReferral"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        handleFieldBlur("wasReferral", checked as boolean);
                      }}
                    />
                  )}
                />
                <Label
                  htmlFor="wasReferral"
                  className="text-xs font-bold leading-3 text-neutral-400"
                >
                  Was this a referral?
                </Label>
              </div>

              {/* Referral Type (only shown if referral checkbox is checked) */}
              {watchedWasReferral && (
                <div>
                  <CustomLabel>Referral Type *</CustomLabel>
                  <Controller
                    name="referralType"
                    control={control}
                    rules={{ required: "Referral type is required" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          handleFieldBlur("referralType", value || "");
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select referral type" />
                        </SelectTrigger>
                        <SelectContent>
                          {REFERRAL_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.referralType && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.referralType.message}
                    </p>
                  )}
                </div>
              )}

              {/* Conditional fields based on escalation type */}
              {showFullForm ? (
                <>
                  {/* Reason for Escalation */}
                  <div>
                    <CustomLabel>Reason for Escalation *</CustomLabel>
                    <Controller
                      name="reasonForEscalation"
                      control={control}
                      rules={{
                        required: "Reason for escalation is required",
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          className="mt-2"
                          onBlur={() =>
                            handleFieldBlur(
                              "reasonForEscalation",
                              field.value || "",
                            )
                          }
                        />
                      )}
                    />
                    {errors.reasonForEscalation && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.reasonForEscalation.message}
                      </p>
                    )}
                  </div>

                  {/* Suicide Assessment Risk */}
                  <div>
                    <CustomLabel>Suicide Assessment Risk *</CustomLabel>
                    <Controller
                      name="suicideAssessmentRisk"
                      control={control}
                      rules={{
                        required: "Suicide assessment risk is required",
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          className="mt-2"
                          onBlur={() =>
                            handleFieldBlur(
                              "suicideAssessmentRisk",
                              field.value || "",
                            )
                          }
                        />
                      )}
                    />
                    {errors.suicideAssessmentRisk && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.suicideAssessmentRisk.message}
                      </p>
                    )}
                  </div>

                  {/* Information Provided by Mentor */}
                  <div>
                    <CustomLabel>Information Provided by Mentor *</CustomLabel>
                    <Controller
                      name="informationProvidedByMentor"
                      control={control}
                      rules={{
                        required: "Information from mentor is required",
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          className="mt-2"
                          onBlur={() =>
                            handleFieldBlur(
                              "informationProvidedByMentor",
                              field.value || "",
                            )
                          }
                        />
                      )}
                    />
                    {errors.informationProvidedByMentor && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.informationProvidedByMentor.message}
                      </p>
                    )}
                  </div>

                  {/* Conversations with Emergency Contact/First Responders */}
                  <div>
                    <CustomLabel>
                      Conversations with Emergency Contact/First Responders *
                    </CustomLabel>
                    <Controller
                      name="conversationsWithEmergencyContact"
                      control={control}
                      rules={{
                        required:
                          "Conversations with emergency contact is required",
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          className="mt-2"
                          onBlur={() =>
                            handleFieldBlur(
                              "conversationsWithEmergencyContact",
                              field.value || "",
                            )
                          }
                        />
                      )}
                    />
                    {errors.conversationsWithEmergencyContact && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.conversationsWithEmergencyContact.message}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                watchedEscalationType === "Other" && (
                  /* Explain Reason for Alert (only for Other type) */
                  <div>
                    <CustomLabel>Explain Reason for Alert *</CustomLabel>
                    <Controller
                      name="explainReasonForAlert"
                      control={control}
                      rules={{
                        required: "Reason for alert is required",
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          className="mt-2"
                          onBlur={() =>
                            handleFieldBlur(
                              "explainReasonForAlert",
                              field.value || "",
                            )
                          }
                        />
                      )}
                    />
                    {errors.explainReasonForAlert && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.explainReasonForAlert.message}
                      </p>
                    )}
                  </div>
                )
              )}

              {/* Next Steps (shown for all types) */}
              <div>
                <CustomLabel>Next Steps *</CustomLabel>
                <Controller
                  name="nextSteps"
                  control={control}
                  rules={{ required: "Next steps are required" }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      className="mt-2"
                      onBlur={() =>
                        handleFieldBlur("nextSteps", field.value || "")
                      }
                    />
                  )}
                />
                {errors.nextSteps && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.nextSteps.message}
                  </p>
                )}
              </div>

              {/* Safety Plan (only for Crisis/Escalation) */}
              {showFullForm && (
                <div>
                  <CustomLabel>Safety Plan *</CustomLabel>
                  <Controller
                    name="safetyPlan"
                    control={control}
                    rules={{ required: "Safety plan is required" }}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        className="mt-2"
                        onBlur={() =>
                          handleFieldBlur("safetyPlan", field.value || "")
                        }
                      />
                    )}
                  />
                  {errors.safetyPlan && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.safetyPlan.message}
                    </p>
                  )}
                </div>
              )}

              {/* Additional Notes (only for Crisis/Escalation) */}
              {showFullForm && (
                <div>
                  <CustomLabel>Additional Notes</CustomLabel>
                  <Controller
                    name="additionalNotes"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        className="mt-2"
                        onBlur={() =>
                          handleFieldBlur("additionalNotes", field.value || "")
                        }
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </Card>

          <div className="flex items-center justify-center">
            <Button variant="default" type="submit" className="mt-4 w-full">
              {completingEscalation && (
                <Loader2 className="mr-2 animate-spin" />
              )}
              Mark complete
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CustomLabel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Label className="text-xs font-bold leading-3 text-neutral-400">
      {children}
    </Label>
  );
};
